"use client";

import ModalWindows from "@/components/ModalWindows";
import { AppPageIntroSlot } from "@/components/layout/AppPageIntroSlot";
import { PortfolioPageOverviewCard } from "@/components/portfolio/portfolio-page/PortfolioPageOverviewCard";
import { PortfolioSelectedEstateSection } from "@/components/portfolio/portfolio-page/PortfolioSelectedEstateSection";
import { useEstateContext } from "@/context/EstateContext";
import { useMenuItemsContext } from "@/context/MenuItemsContext";
import { BackgroundOrbits } from "@/components/BackgroundOrbits";
import { useLanguage } from "@/context/LanguageContext";
import { getEstateCountLabel } from "@/app/portfolio/portfolio-utils";
import PortfolioHomePage from "@/components/index/sections/hero/PortfolioHomePage";
import { useAppContext } from "@/context/AppContext";
import { useSessionContext } from "@/context/SessionContext";
import { NewEstateDialog } from "@/components/modals/CreateNewEstate";
import { hasReachedEstateLimit } from "@/utils/membershipLimits";
import { useHintWindowPreference } from "@/hooks/useHintWindowPreference";

export default function PortfolioPageClient() {
  const { isLoggedIn } = useAppContext();
  const { isInitialLoading } = useSessionContext();

  if (isInitialLoading || !isLoggedIn) {
    return <PortfolioHomePage />;
  }

  return <AuthenticatedPortfolio />;
}

function AuthenticatedPortfolio() {
  const { PROFILE_ITEMS, userInfoGlobal } = useAppContext();
  const { language } = useLanguage();
  const copy = {
    cs: {
      oneProperty: "1 nemovitost",
      propertyCountFew: "{count} nemovitosti",
      propertyCountMany: "{count} nemovitostí",
      analyticsView: "Analytický přehled",
      compactList: "Kompaktní výpis",
      allPropertiesSummary: "Souhrn všech nemovitostí",
      analyticsDescription:
        "Zobrazen je detail ceny, cash flow a predikce vybrané nemovitosti.",
      compactDescription:
        "Zobrazen je přehled transakcí a finančních bloků vybrané nemovitosti.",
      allPropertiesDescription:
        "Vidíte agregovaný přehled nad celým portfoliem bez nutnosti vybírat konkrétní jednotku.",
      emptyDescription:
        "Portfolio se zobrazí hned po přidání první nemovitosti.",
      changeMembership: "Změnit členství",
    },
    en: {
      oneProperty: "1 property",
      propertyCountFew: "{count} properties",
      propertyCountMany: "{count} properties",
      analyticsView: "Analytics view",
      compactList: "Compact list",
      allPropertiesSummary: "Summary of all properties",
      analyticsDescription:
        "The selected property's price detail, cash flow, and forecast are displayed.",
      compactDescription:
        "The selected property's transactions and financial blocks are displayed.",
      allPropertiesDescription:
        "You see an aggregated overview of the whole portfolio without selecting a specific unit.",
      emptyDescription:
        "The portfolio appears as soon as you add the first property.",
      changeMembership: "Change membership",
    },
  }[language];
  const {
    isGraphView,
    allEstateList,
    selectedEstate,
    setSelectedEstate,
    analysis,
    portfolioEstimatedValue,
    isLoadingEstate,
  } = useEstateContext();
  const { USER_ESTATE_ITEMS, setIsOpen, setSelectedItem } =
    useMenuItemsContext();

  const estates = allEstateList ?? [];
  const estateCount = estates.length;
  const isEstateLimitReached = hasReachedEstateLimit(
    estateCount,
    userInfoGlobal?.membership,
  );
  const hasEstates = estateCount > 0;
  const activeViewLabel = selectedEstate
    ? isGraphView
      ? copy.analyticsView
      : copy.compactList
    : copy.allPropertiesSummary;
  const activeViewDescription = selectedEstate
    ? isGraphView
      ? copy.analyticsDescription
      : copy.compactDescription
    : hasEstates
      ? copy.allPropertiesDescription
      : copy.emptyDescription;

  const handleOpenCreateEstate = () => {
    setSelectedItem(
      isEstateLimitReached ? PROFILE_ITEMS[0] : USER_ESTATE_ITEMS[0],
    );
    setIsOpen(true);
  };
  const { isOpen: isHintOpen, toggle: toggleHint } = useHintWindowPreference(
    "portfolioPerformance",
  );

  return (
    <section className="app-page-section app-page-section--mobile-edge-to-edge overflow-hidden">
      <BackgroundOrbits />
      <div className="app-page-container flex flex-col gap-4">
        <AppPageIntroSlot hasPortfolioEstate={hasEstates} />
        <div data-tour="portfolio-selection">
          <PortfolioPageOverviewCard
            estateCount={estateCount}
            estateCountLabel={getEstateCountLabel(estateCount, copy)}
            activeViewLabel={activeViewLabel}
            activeViewDescription={activeViewDescription}
            isLoading={isLoadingEstate}
            estates={estates}
            selectedEstateId={selectedEstate?.id ?? null}
            currentEstateValue={analysis.avgPriceSell}
            portfolioEstimatedValue={portfolioEstimatedValue}
            onCreateEstate={handleOpenCreateEstate}
            createActionLabel={
              isEstateLimitReached ? copy.changeMembership : undefined
            }
            onSelectEstate={setSelectedEstate}
            emptyContent={
              estateCount === 0 && !isLoadingEstate ? (
                <div className="mx-auto w-full max-w-[425px]">
                  <NewEstateDialog />
                </div>
              ) : undefined
            }
          />
        </div>
        {selectedEstate ? (
          <div data-tour="portfolio-detail">
            <PortfolioSelectedEstateSection
              hasSelectedEstate
              isGraphView={isGraphView}
              isHintOpen={isHintOpen}
              onToggleHint={toggleHint}
            />
          </div>
        ) : null}

        <ModalWindows />
      </div>
    </section>
  );
}
